import { useState } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get("next") || "/";
  const [email, setEmail] = useState("officeofrsb@gmail.com");
  const [password, setPassword] = useState("RSB@123");
  const [otp, setOtp] = useState("");
  const [require2fa, setRequire2fa] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={nextUrl} replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password, require2fa ? otp : undefined);
    setLoading(false);
    if (r.ok) {
      toast.success("Signed in");
      navigate(nextUrl, { replace: true });
    } else if (r.require_2fa) {
      setRequire2fa(true);
      toast.message("Enter the 6-digit code from your authenticator app.");
    } else {
      toast.error(r.error);
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-white">
      {/* Left visual panel */}
      <div
        className="hidden lg:flex lg:col-span-3 relative overflow-hidden"
        style={{ backgroundColor: "#0f172a" }}
      >
        <div
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "url(https://customer-assets.emergentagent.com/job_complaint-lifecycle-1/artifacts/pa6wutoc_ChatGPT%20Image%20Jul%204%2C%202026%20at%2002_50_30%20PM.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.25) 45%, rgba(234,88,12,0.30) 100%)",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_complaint-lifecycle-1/artifacts/utuwjsdw_ChatGPT%20Image%20Jul%204%2C%202026%20at%2002_54_59%20PM.png"
              alt="Office of RSB emblem"
              className="w-10 h-10 rounded-full object-cover ring-1 ring-white/25 shadow-md"
            />
            <div>
              <div className="text-sm font-semibold tracking-tight">
                Office of RSB
              </div>
              <div className="text-xs text-white/70 font-mono-cms">
                Complaint Management System
              </div>
            </div>
          </div>
          <div className="max-w-lg">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-300/90 font-semibold mb-4">
              Module 1 · Grievance Redressal
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
              A single window for every citizen&apos;s voice.
            </h1>
            <p className="mt-6 text-white/70 text-base max-w-md leading-relaxed">
              Register, track and resolve complaints across 23 departments with
              a complete lifecycle timeline, media evidence and department-wise
              accountability.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 text-white/80">
              <div>
                <div className="text-3xl font-bold text-white">23</div>
                <div className="text-xs mt-1 uppercase tracking-wider">
                  Departments
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">9</div>
                <div className="text-xs mt-1 uppercase tracking-wider">
                  Lifecycle Stages
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">∞</div>
                <div className="text-xs mt-1 uppercase tracking-wider">
                  Progress Updates
                </div>
              </div>
            </div>
          </div>
          <div className="text-xs text-white/50 font-mono-cms">
            v1.0 · Secure · Bilingual
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex items-center justify-center p-8 sm:p-12 grain-bg">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_complaint-lifecycle-1/artifacts/utuwjsdw_ChatGPT%20Image%20Jul%204%2C%202026%20at%2002_54_59%20PM.png"
              alt="Office of RSB emblem"
              className="w-10 h-10 rounded-full object-cover ring-1 ring-slate-200"
            />
            <div className="text-sm font-bold text-slate-900">
              Office of RSB
            </div>
          </div>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-3">
              Sign in
            </div>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">
              Welcome back.
            </h2>
            <p className="text-slate-500 text-sm mt-2">
              Access the complaint management dashboard.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700 text-sm font-semibold">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 text-sm font-semibold">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1.5 h-11"
              />
            </div>
            {require2fa && (
              <div>
                <Label htmlFor="otp" className="text-slate-700 text-sm font-semibold">
                  6-digit authenticator code
                </Label>
                <Input
                  id="otp"
                  data-testid="login-otp"
                  inputMode="numeric"
                  autoFocus
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                  className="mt-1.5 h-11 text-center font-mono-cms text-lg tracking-widest"
                  placeholder="123456"
                />
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : require2fa ? "Verify & Sign In" : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 rounded-md border border-slate-200 bg-white/60 p-3 text-xs text-slate-500">
            <div>
              <span className="font-semibold text-slate-700">Admin:</span>{" "}
              officeofrsb@gmail.com / RSB@123
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-slate-500">
            Need an account?{" "}
            <Link
              to="/register"
              data-testid="link-register"
              className="text-orange-600 font-semibold hover:underline"
            >
              Create one
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
